/**
 * DEV-260 — etapa do formulário. Quais perguntas, em que ordem e quais são
 * obrigatórias vem da definição do Core; esta tela só desenha, valida ao sair
 * do campo e no Avançar, e grava a etapa antes de seguir (FR-010).
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, StepSkeleton } from '@eureca/ui';
import type { SurveyRendererHandle } from '@eureca/surveyjs-wrapper';
import type { AnswerIssue } from '../shared/api';
import type {
  CustomFormPage,
  FormDefinition,
  JobDescription,
  StandardFormPage,
} from '../shared/definition';
import { applyDraft, loadDraft, recordDraftChange } from '../shared/draft';
import { FIELD_MESSAGES } from '../shared/field-copy';
import { isQuestionShown, type Answers } from '../shared/form-rules';
import { AppBar, ProgramHeader } from '../shared/FormHeader';
import {
  applyAnswerChange,
  isRequiredNow,
  nextPageName,
  pageAnswersForSave,
  pageIssues,
  pageWarnings,
  previousPageName,
  progressPercent,
} from '../shared/page-rules';
import { omitKeys } from '../shared/objects';
import { GraveErrorScreen, NoConnectionScreen } from '../shared/SystemScreens';
import { useFormSession } from '../shared/use-form-session';
import { QuestionField } from './fields/QuestionField';
import { nonCriticalToast, useStepSave } from './use-step-save';

/**
 * DEV-261 FR-017 — o corpo da página adicional é um chunk à parte. Hoje o
 * `survey-core` já vem no chunk principal do recruiter (o construtor de formulários
 * do consultor o importa estaticamente), então a economia é só deste arquivo; separar
 * o bundle da rota pública do resto do app é card à parte.
 */
const CustomStepBody = lazy(async () => ({
  default: (await import('./CustomStepBody')).CustomStepBody,
}));

/** Campos que o Figma põe lado a lado (22606, 22625). Só apresentação. */
const SIDE_BY_SIDE: readonly [string, string][] = [
  ['valor_minimo', 'valor_maximo'],
  ['data_minima_formacao', 'data_maxima_formacao'],
  // 22645: "Linha com dois Inputs".
  ['idade_minima', 'idade_maxima'],
];

/** Agrupa as perguntas visíveis em linhas: par conhecido e consecutivo vira uma linha de duas. */
function toRows<T extends { name: string }>(questions: T[]): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < questions.length; i += 1) {
    const current = questions[i] as T;
    const next = questions[i + 1];
    if (next && SIDE_BY_SIDE.some(([a, b]) => a === current.name && b === next.name)) {
      rows.push([current, next]);
      i += 1;
    } else {
      rows.push([current]);
    }
  }
  return rows;
}

export interface StepPageProps {
  tenant: string;
  formId: string;
  pageName: string;
  /** Veio do Resumo ("Editar"): o Avançar devolve para lá. */
  returnToSummary?: boolean;
}

export function StepPage(props: StepPageProps) {
  const form = useFormSession(props.tenant, props.formId);
  const navigate = useNavigate();

  const page = form.definition?.pages.find((p) => p.name === props.pageName);
  const firstPage = form.definition?.pages[0]?.name;

  // Etapa oculta pela configuração (ou nome inválido): segue para a primeira visível.
  useEffect(() => {
    if (form.definition && !page && firstPage) {
      void navigate({
        to: '/descricao-vaga/$tenant/$formId/etapa/$pageName',
        params: { tenant: props.tenant, formId: props.formId, pageName: firstPage },
        replace: true,
      });
    }
  }, [firstPage, form.definition, navigate, page, props.formId, props.tenant]);

  // Descrição já enviada: o fluxo acabou (FR-019).
  const submitted = form.description?.status === 'submitted';
  useEffect(() => {
    if (submitted) {
      void navigate({
        to: '/descricao-vaga/$tenant/$formId/sucesso',
        params: { tenant: props.tenant, formId: props.formId },
        replace: true,
      });
    }
  }, [navigate, props.formId, props.tenant, submitted]);

  if (form.failure === 'offline') return <NoConnectionScreen onRetry={form.retry} />;
  // Definição sem etapa nenhuma não tem para onde ir (o Core sempre projeta ao menos duas).
  if (form.definition && form.definition.pages.length === 0) {
    return <GraveErrorScreen onRetry={form.retry} />;
  }
  if (form.failure === 'grave') return <GraveErrorScreen onRetry={form.retry} />;
  if (
    form.loading ||
    !form.definition ||
    !form.description ||
    !page ||
    !form.session ||
    submitted
  ) {
    return <StepLoading />;
  }

  const shared = {
    ...props,
    definition: form.definition,
    description: form.description,
    sessionToken: form.session.sessionToken,
  };
  return page.kind === 'standard' ? (
    <StandardStep key={page.name} {...shared} page={page} />
  ) : (
    <CustomStep key={page.name} {...shared} page={page} />
  );
}

function StepLoading() {
  return (
    <div className="min-h-dvh bg-background">
      <AppBar />
      <div className="mx-auto max-w-[640px] py-12">
        <StepSkeleton />
      </div>
    </div>
  );
}

function StepLayout({
  definition,
  page,
  children,
}: {
  definition: FormDefinition;
  page: StandardFormPage | CustomFormPage;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-dvh bg-background">
      <AppBar />
      <main
        className="mx-auto flex max-w-[640px] flex-col gap-10 py-12"
        data-testid={`step-${page.name}`}
      >
        <ProgramHeader
          programName={definition.program.name}
          logoUrl={definition.program.logoUrl}
          stepTitle={page.title}
          progress={progressPercent(definition, page.name)}
        />
        {children}
      </main>
    </div>
  );
}

/**
 * Voltar em toda etapa com anterior (QA 08/10/2026); último passo antes do Resumo é
 * "Salvar". Com `onPrimary` o botão primário age por clique (página adicional, que não
 * é um `<form>`); sem ele é o submit do formulário da etapa padrão.
 */
function StepFooter({
  showBack,
  primaryLabel,
  loading,
  disabled = false,
  onBack,
  onPrimary,
}: {
  showBack: boolean;
  primaryLabel: string;
  loading: boolean;
  /** Primário inerte (página adicional antes de o corpo montar). */
  disabled?: boolean;
  onBack: () => void;
  onPrimary?: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      {showBack ? (
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="px-8"
          onClick={onBack}
          data-testid="step-back"
        >
          {FIELD_MESSAGES.voltar}
        </Button>
      ) : (
        <span />
      )}
      <Button
        type={onPrimary ? 'button' : 'submit'}
        size="lg"
        className="px-8"
        loading={loading}
        disabled={disabled}
        onClick={onPrimary}
        data-testid="step-advance"
      >
        {primaryLabel}
      </Button>
    </div>
  );
}

function primaryLabelFor(definition: FormDefinition, pageName: string, returnToSummary?: boolean) {
  return !returnToSummary && nextPageName(definition, pageName) === null
    ? FIELD_MESSAGES.salvar
    : FIELD_MESSAGES.avancar;
}

function CustomStep(
  props: StepPageProps & {
    page: CustomFormPage;
    definition: FormDefinition;
    description: JobDescription;
    sessionToken: string;
  }
) {
  const { page, description } = props;
  // Página adicional: o SurveyJS não mostra erro do servidor no campo; vira toast.
  const step = useStepSave({ ...props, pageName: page.name }, (issues) => {
    if (issues[0]) nonCriticalToast(issues[0].message);
  });
  const initial = useMemo(
    () => applyDraft(description.answers, loadDraft(description.id)),
    [description.answers, description.id]
  );
  const lastDraft = useRef<Answers>(
    Object.fromEntries(
      page.questionNames.map((q) => [q, initial[q]]).filter(([, v]) => v !== undefined)
    )
  );
  // QA 09/10/2026: Voltar e Avançar na mesma linha, como nas etapas padrão. O
  // Avançar valida e conclui o survey (`tryComplete`); a gravação vem pelo `onSubmit`.
  // O botão só habilita quando o corpo (chunk sob demanda) montou — antes disso um
  // clique não teria a quem chamar. O handle fica num ref; só o "pronto" vira estado,
  // e uma vez só (um callback ref em setState re-renderizaria a cada handle novo).
  const body = useRef<SurveyRendererHandle | null>(null);
  const [ready, setReady] = useState(false);
  return (
    <StepLayout definition={props.definition} page={page}>
      {/* QA 08/10/2026: o layout já desenha cabeçalho e rodapé — o esqueleto só simula os campos. */}
      <Suspense fallback={<StepSkeleton header={false} actions={false} />}>
        <CustomStepBody
          ref={(handle) => {
            body.current = handle;
            if (handle) setReady(true);
          }}
          page={page}
          initialAnswers={initial}
          onDraft={(data) => {
            recordDraftChange(description.id, lastDraft.current, data);
            lastDraft.current = data;
          }}
          onSubmit={async (data) => {
            if (!step.begin()) throw new Error('em andamento');
            const body: Answers = Object.fromEntries(
              page.questionNames.map((q) => [q, data[q] ?? null])
            );
            await step.save(body);
          }}
        />
      </Suspense>
      <StepFooter
        showBack={!!props.returnToSummary || previousPageName(props.definition, page.name) !== null}
        primaryLabel={primaryLabelFor(props.definition, page.name, props.returnToSummary)}
        loading={step.isPending}
        disabled={!ready}
        onBack={step.goBack}
        onPrimary={() => {
          if (!step.isPending) body.current?.tryComplete();
        }}
      />
    </StepLayout>
  );
}

interface StandardStepProps extends StepPageProps {
  page: StandardFormPage;
  definition: FormDefinition;
  description: JobDescription;
  sessionToken: string;
}

function StandardStep({
  tenant,
  formId,
  page,
  definition,
  description,
  sessionToken,
  returnToSummary,
}: StandardStepProps) {
  const [answers, setAnswers] = useState<Answers>(() =>
    applyDraft(description.answers, loadDraft(description.id))
  );
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  // Vindo do Resumo (Editar ou envio incompleto), os problemas aparecem de cara.
  const [showAll, setShowAll] = useState(!!returnToSummary);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);

  const issues = useMemo(
    () => pageIssues(definition, page.name, answers),
    [answers, definition, page.name]
  );
  const warnings = pageWarnings(answers);
  const errorFor = (question: string): string | undefined => {
    if (serverErrors[question]) return serverErrors[question];
    if (!showAll && !touched.has(question)) return undefined;
    return issues.find((i) => i.question === question)?.message;
  };

  function focusQuestion(question: string) {
    const root = formRef.current?.querySelector<HTMLElement>(`[data-question="${question}"]`);
    root
      ?.querySelector<HTMLElement>('input, textarea, button, [tabindex]:not([tabindex="-1"])')
      ?.focus();
  }

  const step = useStepSave(
    { tenant, formId, pageName: page.name, definition, description, sessionToken, returnToSummary },
    (serverIssues: AnswerIssue[]) => {
      setServerErrors(Object.fromEntries(serverIssues.map((i) => [i.question, i.message])));
      const first = serverIssues[0]?.question;
      if (first) focusQuestion(first);
    }
  );

  function change(question: string, value: unknown) {
    setAnswers((prev) => {
      const next = applyAnswerChange(prev, question, value);
      recordDraftChange(description.id, prev, next);
      return next;
    });
    setServerErrors((prev) => {
      return question in prev ? omitKeys(prev, [question]) : prev;
    });
  }

  function advance() {
    if (issues.length > 0) {
      setShowAll(true);
      const first = issues[0]?.question;
      if (first) focusQuestion(first);
      return;
    }
    if (!step.begin()) return;
    step.save(pageAnswersForSave(definition, page.name, answers)).catch(() => undefined);
  }

  const visible = page.questions.filter((q) => isQuestionShown(q.name, answers));

  return (
    <StepLayout definition={definition} page={page}>
      <form
        ref={formRef}
        noValidate
        className="flex flex-col gap-10"
        onSubmit={(e) => {
          e.preventDefault();
          advance();
        }}
      >
        <div className="flex flex-col gap-6">
          {toRows(visible).map((row) => (
            <div
              key={row[0]?.name}
              className={row.length > 1 ? 'grid grid-cols-2 gap-4' : undefined}
            >
              {row.map((q) => (
                <QuestionField
                  key={q.name}
                  question={q}
                  session={sessionToken}
                  answers={answers}
                  required={isRequiredNow(definition, answers, q.name)}
                  error={errorFor(q.name)}
                  warning={warnings[q.name]}
                  onChange={(v) => change(q.name, v)}
                  onBlur={() =>
                    setTouched((prev) => (prev.has(q.name) ? prev : new Set(prev).add(q.name)))
                  }
                />
              ))}
            </div>
          ))}
        </div>
        <StepFooter
          // QA 08/10/2026: toda etapa com anterior tem Voltar (e vindo do Resumo também).
          showBack={!!returnToSummary || previousPageName(definition, page.name) !== null}
          primaryLabel={primaryLabelFor(definition, page.name, returnToSummary)}
          loading={step.isPending}
          onBack={step.goBack}
        />
      </form>
    </StepLayout>
  );
}
